/**
 * Volume renderer. One jittered ray-marched sample is accumulated per frame into
 * an HDR buffer, then tone mapped in a present pass — so a still image keeps
 * refining itself while nothing is moving.
 */

import { Program, RenderTarget, ScreenQuad, type GLContext } from './gl';
import { shaders } from '../shaders';
import { clamp } from './mat4';
import { hexToRgb, type Settings } from './state';
import type { OrbitCamera } from './camera';
import type { Volume } from './volume';

export interface BoxLayout {
  boxMin: Float32Array;
  boxMax: Float32Array;
  /** Logical layer at boxMin.z / boxMax.z. Reversed in 'consume' mode. */
  layerLo: number;
  layerHi: number;
  /** The intact block, in layer units. */
  solidLo: number;
  solidHi: number;
  /** The peeled layers that linger, in layer units. Empty when solidLo > solidHi. */
  traceLo: number;
  traceHi: number;
  /** Layers spanned by the box. */
  span: number;
  steps: number;
}

export interface RenderStats {
  samples: number;
  steps: number;
  layerLo: number;
  layerHi: number;
}

function halton(index: number, base: number): number {
  let result = 0;
  let f = 1 / base;
  let i = index;
  while (i > 0) {
    result += f * (i % base);
    i = Math.floor(i / base);
    f /= base;
  }
  return result;
}

export class VolumeRenderer {
  private readonly gl: WebGL2RenderingContext;
  private readonly raymarch: Program;
  private readonly present: Program;
  private readonly wire: Program;
  private readonly lineVao: WebGLVertexArrayObject;

  private accum: RenderTarget | null = null;
  private samples = 0;
  private frameCounter = 0;
  private dirty = true;

  private readonly boxMin = new Float32Array(3);
  private readonly boxMax = new Float32Array(3);
  private readonly dimsVec = new Float32Array(3);
  private readonly resolutionVec = new Float32Array(2);
  private readonly bgVec = new Float32Array(3);
  private readonly wireVec = new Float32Array(3);

  width = 1;
  height = 1;

  constructor(
    private readonly ctx: GLContext,
    private readonly quad: ScreenQuad,
  ) {
    this.gl = ctx.gl;
    this.raymarch = new Program(this.gl, shaders.quadVert, shaders.raymarchFrag, 'raymarch');
    this.present = new Program(this.gl, shaders.quadVert, shaders.presentFrag, 'present');
    this.wire = new Program(this.gl, shaders.wireVert, shaders.wireFrag, 'wire');

    const vao = this.gl.createVertexArray();
    if (!vao) throw new Error('createVertexArray failed');
    this.lineVao = vao;
  }

  /** Forces accumulation to restart on the next frame. */
  invalidate(): void {
    this.dirty = true;
  }

  get sampleCount(): number {
    return this.samples;
  }

  setSize(cssWidth: number, cssHeight: number, dpr: number, scale: number): void {
    const width = Math.max(1, Math.floor(cssWidth * dpr * scale));
    const height = Math.max(1, Math.floor(cssHeight * dpr * scale));
    if (width === this.width && height === this.height && this.accum) return;

    this.width = width;
    this.height = height;
    this.ctx.canvas.width = width;
    this.ctx.canvas.height = height;

    this.accum?.dispose();
    const format = this.ctx.caps.colorBufferFloat ? this.gl.RGBA16F : this.gl.RGBA8;
    this.accum = new RenderTarget(this.gl, width, height, format, this.gl.NEAREST);
    this.invalidate();
  }

  /** Where the block sits in world space, and which logical layers its t axis covers. */
  computeLayout(volume: Volume, settings: Settings): BoxLayout {
    const { peel, look } = settings;
    const count = Math.max(1, volume.count);
    const total = Math.max(1, count - 1);

    const head = clamp(peel.head, 0, total);
    const consuming = peel.mode === 'consume';

    // The intact block sits ahead of the playhead when consuming and behind it when
    // accumulating; the trace always occupies the layers on the other side.
    let solidLo: number;
    let solidHi: number;
    let traceLo: number;
    let traceHi: number;
    if (consuming) {
      solidLo = head;
      solidHi = clamp(head + peel.slabDepth, head, total);
      traceHi = head - 1;
      traceLo = clamp(head - peel.traceDepth, 0, total);
    } else {
      solidHi = head;
      solidLo = clamp(head - peel.slabDepth, 0, head);
      traceLo = head + 1;
      traceHi = clamp(head + peel.traceDepth, 0, total);
    }
    const hasTrace = peel.traceAlpha > 0 && peel.traceDepth > 0 && traceHi >= traceLo;

    const occupiedLo = hasTrace ? Math.min(solidLo, traceLo) : solidLo;
    const occupiedHi = hasTrace ? Math.max(solidHi, traceHi) : solidHi;
    const visibleLo = peel.fitBox ? occupiedLo : 0;
    const visibleHi = peel.fitBox ? Math.max(occupiedHi, occupiedLo + 1e-3) : total;
    const span = visibleHi - visibleLo;

    const hx = 0.5;
    const hy = 0.5 * (volume.height / Math.max(1, volume.width));
    const hzFull = 0.5 * look.depthScale;

    const fraction = peel.fitBox ? clamp(span / total, 1e-3, 1) : 1;
    const thickness = 2 * hzFull * fraction;

    let zMin: number;
    let zMax: number;
    if (peel.anchor === 'front') {
      zMax = hzFull;
      zMin = hzFull - thickness;
    } else if (peel.anchor === 'back') {
      zMin = -hzFull;
      zMax = -hzFull + thickness;
    } else {
      zMax = thickness * 0.5;
      zMin = -thickness * 0.5;
    }

    this.boxMin[0] = -hx;
    this.boxMin[1] = -hy;
    this.boxMin[2] = zMin;
    this.boxMax[0] = hx;
    this.boxMax[1] = hy;
    this.boxMax[2] = zMax;

    // While consuming, t runs away from the +z face so the layer being stripped stays
    // the one facing the camera and the untouched block recedes behind it.
    const layerLo = consuming ? visibleHi : visibleLo;
    const layerHi = consuming ? visibleLo : visibleHi;
    const layerSpan = Math.max(Math.abs(layerHi - layerLo), 1e-3);

    let steps: number;
    if (look.autoSteps) {
      // One sample per layer along the longest possible path through the block.
      const ex = this.boxMax[0] - this.boxMin[0];
      const ey = this.boxMax[1] - this.boxMin[1];
      const ez = this.boxMax[2] - this.boxMin[2];
      const diagonal = Math.hypot(ex, ey, ez);
      const layerThickness = Math.max(ez / layerSpan, 1e-5);
      steps = Math.ceil(diagonal / layerThickness);
    } else {
      steps = look.steps;
    }
    steps = clamp(Math.round(steps), 24, 1024);

    return {
      boxMin: this.boxMin,
      boxMax: this.boxMax,
      layerLo,
      layerHi,
      solidLo,
      solidHi,
      traceLo: hasTrace ? traceLo : 0,
      traceHi: hasTrace ? traceHi : -1,
      span,
      steps,
    };
  }

  render(volume: Volume, settings: Settings, camera: OrbitCamera): RenderStats {
    const { gl } = this;
    const accum = this.accum;
    if (!accum) {
      return { samples: 0, steps: 0, layerLo: 0, layerHi: 0 };
    }

    const { look } = settings;
    const maxSamples = look.accumulate && this.ctx.caps.colorBufferFloat ? Math.max(1, look.maxSamples) : 1;

    if (this.dirty) {
      accum.clear(0, 0, 0, 0);
      this.samples = 0;
      this.dirty = false;
    }

    const layout = this.computeLayout(volume, settings);
    const hasContent = volume.count > 0 && !!volume.videoTexture && !!volume.stainTexture;

    if (this.samples < maxSamples && hasContent) {
      this.frameCounter++;
      const jx = maxSamples > 1 ? (halton(this.samples + 1, 2) - 0.5) * 2 : 0;
      const jy = maxSamples > 1 ? (halton(this.samples + 1, 3) - 0.5) * 2 : 0;
      const view = camera.computeView(this.width / this.height, jx / this.width, jy / this.height);

      accum.bind();
      gl.enable(gl.BLEND);
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFunc(gl.ONE, gl.ONE);

      this.dimsVec[0] = volume.width;
      this.dimsVec[1] = volume.height;
      this.dimsVec[2] = volume.layers;

      this.raymarch
        .use()
        .set('uInvViewProj', view.invViewProj)
        .set('uCamPos', view.position)
        .set('uBoxMin', layout.boxMin)
        .set('uBoxMax', layout.boxMax)
        .set('uDims', this.dimsVec)
        .set('uRing', volume.ringOffset)
        .set('uLayerLo', layout.layerLo)
        .set('uLayerHi', layout.layerHi)
        .set('uHead', clamp(settings.peel.head, 0, Math.max(0, volume.count - 1)))
        .set('uSolidLo', layout.solidLo)
        .set('uSolidHi', layout.solidHi)
        .set('uTraceLo', layout.traceLo)
        .set('uTraceHi', layout.traceHi)
        .set('uTraceAlpha', layout.traceHi >= layout.traceLo ? settings.peel.traceAlpha : 0)
        .set('uTraceFade', settings.peel.traceFade)
        .set('uTraceStain', settings.peel.traceStain)
        .set('uSteps', layout.steps)
        .set('uTrilinear', look.trilinear ? 1 : 0)
        .set('uColorMode', look.colorMode)
        .set('uDensityMode', look.densityMode)
        .set('uDensity', look.density)
        .set('uStainMix', look.stainMix)
        .set('uStainGlow', look.stainGlow)
        .set('uCutoff', look.cutoff)
        .set('uFrontOpacity', look.frontOpacity)
        .set('uDepthShade', look.depthShade)
        .set('uJitter', look.jitter)
        .set('uFrame', this.frameCounter)
        .set('uExposure', look.exposure);

      this.raymarch.texture('uVideo', 0, gl.TEXTURE_3D, volume.videoTexture);
      this.raymarch.texture('uStain', 1, gl.TEXTURE_3D, volume.stainTexture);
      this.quad.draw();

      if (look.wire) {
        const [wr, wg, wb] = hexToRgb(look.wireColor);
        this.wireVec[0] = wr;
        this.wireVec[1] = wg;
        this.wireVec[2] = wb;
        this.wire
          .use()
          .set('uViewProj', view.viewProj)
          .set('uBoxMin', layout.boxMin)
          .set('uBoxMax', layout.boxMax)
          .set('uColor', this.wireVec)
          .set('uAlpha', look.wireAlpha);
        gl.bindVertexArray(this.lineVao);
        gl.drawArrays(gl.LINES, 0, 24);
        gl.bindVertexArray(null);
      }

      gl.disable(gl.BLEND);
      this.samples++;
    }

    // Present.
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.width, this.height);
    gl.disable(gl.BLEND);

    const [br, bg2, bb] = hexToRgb(look.background);
    this.bgVec[0] = br;
    this.bgVec[1] = bg2;
    this.bgVec[2] = bb;
    this.resolutionVec[0] = this.width;
    this.resolutionVec[1] = this.height;

    this.present
      .use()
      .set('uInvSamples', 1 / Math.max(1, this.samples))
      .set('uBackground', this.bgVec)
      .set('uBackgroundFalloff', look.backgroundFalloff)
      .set('uVignette', look.vignette)
      .set('uGamma', look.gamma)
      .set('uTonemap', look.tonemap)
      .set('uResolution', this.resolutionVec);
    this.present.texture('uAccum', 0, gl.TEXTURE_2D, accum.texture);
    this.quad.draw();

    return {
      samples: this.samples,
      steps: layout.steps,
      layerLo: layout.layerLo,
      layerHi: layout.layerHi,
    };
  }

  dispose(): void {
    this.accum?.dispose();
    this.accum = null;
    this.raymarch.dispose();
    this.present.dispose();
    this.wire.dispose();
    this.gl.deleteVertexArray(this.lineVao);
  }
}
